import { useEffect } from 'react';

const s = {
  section: { marginBottom: 22 },
  h: { fontSize: 13, fontWeight: 700, color: '#F97316', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
  p: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 6px' },
  step: { fontSize: 13, color: 'var(--text)', lineHeight: 1.6, margin: '3px 0', paddingLeft: 20, position: 'relative' },
  warn: {
    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#EF4444', lineHeight: 1.5, marginBottom: 10,
  },
  tip: {
    background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
    borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#F97316', lineHeight: 1.5, marginBottom: 10,
  },
  info: {
    background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#818cf8', lineHeight: 1.5, marginBottom: 10,
  },
};

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.h}>{title}</div>
      {children}
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol style={{ margin: '0 0 6px', paddingLeft: 20 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, marginBottom: 3 }}>{item}</li>
      ))}
    </ol>
  );
}

function Table({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 6 }}>
      <tbody>
        {rows.map(([label, desc], i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--surface2)' }}>
            <td style={{ padding: '6px 8px 6px 0', color: 'var(--text)', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', width: '38%' }}>{label}</td>
            <td style={{ padding: '6px 0 6px 8px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const CONTENT = {
  dashboard: (
    <>
      <div style={s.info}>Read-only screen. Do not act here — go to the correct screen to take action.</div>
      <Section title="Alert banners — what to do">
        <Table rows={[
          ['Yellow — licenses', 'Click "Review →" → Driver Verification'],
          ['Purple — photos', 'Click "Review →" → Driver Verification > Photo Pending'],
          ['Red — overdue jobs', 'Click "Review →" → Job Management'],
          ['Red — reports', 'Click "Review →" → Reports Queue'],
        ]} />
      </Section>
      <Section title="Cards explained">
        <Table rows={[
          ['Pending Licenses', 'Drivers waiting for CDL approval'],
          ['Photo Review', 'Profile photos needing approval'],
          ['Overdue Jobs', 'Jobs past end date, not marked complete'],
          ['Pending Reports', 'User complaints awaiting review'],
        ]} />
      </Section>
      <Section title="Activity Feed">
        <p style={s.p}><strong>Recent Signups</strong> — info only, no action needed.</p>
        <p style={s.p}><strong>Function Logs</strong> — system actions. If you see errors, tell your manager.</p>
      </Section>
    </>
  ),

  drivers: (
    <>
      <div style={s.tip}>Automatic emails are sent to drivers on every approval or rejection — you do NOT need to email them.</div>
      <Section title="Daily start — filters">
        <Table rows={[
          ['⏳ CDL Pending', 'Start here every day — licenses to review'],
          ['📸 Photo Pending', 'Profile photos to approve'],
          ['📄 Abstract Pending', 'Driver abstracts to review'],
          ['📅 Expired', 'Expired licenses to flag'],
          ['All Drivers', 'Use only to search a specific person'],
        ]} />
      </Section>
      <Section title="Approving a CDL">
        <p style={s.p}>Only approve when all three photos are clear and readable, name matches account, and license is not expired.</p>
        <Steps items={['Click ✅ Approve CDL', 'Done — approval email is sent automatically']} />
      </Section>
      <Section title="Rejecting a CDL">
        <Steps items={[
          'Click ❌ Reject CDL',
          'Write a clear reason (e.g. "Photo is blurry. Please re-upload.")',
          'Click Reject & Send Email',
        ]} />
        <div style={s.warn}>After 3 rejections the account locks. Driver must email support@crewboard.ca to unlock.</div>
      </Section>
      <Section title="Driver's Abstract">
        <p style={s.p}>Must be an official government document, <strong>less than 30 days old</strong>, name matches account.</p>
        <Steps items={['Use 📄 Abstract Pending filter', 'Open full size to read it', 'Approve or Reject with reason']} />
      </Section>
      <Section title="Other buttons">
        <Table rows={[
          ['⛔ Suspend', 'Locks driver out — manager approval only'],
          ['🔓 Reactivate', 'Restores access — manager approval only'],
          ['🚩 Clear No-Shows', 'Only after manager reviews reports'],
          ['🗑️ Delete', 'PERMANENT — direct manager instruction only'],
        ]} />
      </Section>
    </>
  ),

  hirers: (
    <>
      <div style={s.tip}>You must verify the business in the government registry before approving.</div>
      <Section title="Filters">
        <Table rows={[
          ['⏳ Pending', 'Hirers waiting for review — start here'],
          ['✅ Verified', 'Already approved — reference only'],
          ['❌ Rejected', 'Rejected hirers'],
        ]} />
      </Section>
      <Section title="Approval steps">
        <Steps items={[
          'Check Business Number and Registration Type on the card',
          'Click the registry button (Corporations Canada for federal, or province)',
          'Confirm: name matches, business is active, BN matches',
          'Click 👁️ View Document — confirm it\'s an official business document',
          'Click ✅ Approve',
        ]} />
      </Section>
      <Section title="Rejection reasons">
        <p style={s.p}>Reject if: document is not a real business doc, business not found in registry, BN mismatch, document blurry or unreadable. Hirers also get 3 attempts before lockout.</p>
      </Section>
      <Section title="Fast Pay badge ⚡">
        <Table rows={[
          ['Auto-granted', 'Rating ≥ 4.5 stars across 3+ reviews'],
          ['Manual grant', 'Confirmed fast payer, rating ≥ 3.5'],
          ['Revoke', 'Multiple late payment reports'],
        ]} />
        <p style={s.p}>Hirer is NOT notified when badge is granted or revoked.</p>
      </Section>
      <Section title="Other buttons">
        <Table rows={[
          ['⛔ Suspend', 'Manager instruction only'],
          ['🔓 Reactivate', 'Manager instruction only'],
          ['🗑️ Delete', 'PERMANENT — direct manager instruction only'],
        ]} />
      </Section>
    </>
  ),

  disputes: (
    <>
      <div style={s.info}>A dispute is opened automatically when a hirer reports a driver for a no-show, a late cancellation, or a job that "didn't happen." The driver gets 48 hours to respond. Your job is to decide the disputed ones fairly.</div>
      <Section title="How a dispute flows">
        <Steps items={[
          'Hirer reports a no-show / late cancel / "didn\'t happen" → a dispute is created',
          'The driver is notified and has 48 hours to respond with their side',
          'If the driver responds → it lands in "Needs Review" for you to decide',
          'If the driver does NOT respond in 48h → the system auto-upholds it (penalty applies). No action needed from you',
        ]} />
      </Section>
      <Section title="The three tabs">
        <Table rows={[
          ['Needs Review', 'Driver has responded — these need YOUR decision. Start here.'],
          ['Awaiting Driver', 'Within the 48h window, driver hasn\'t replied yet. Usually leave alone — auto-resolves if they stay silent. You can act early only on manager instruction.'],
          ['Resolved', 'Already upheld or dismissed — reference only.'],
        ]} />
      </Section>
      <Section title="Reading a dispute card">
        <Table rows={[
          ['Type', 'No-show / Late cancellation / Job not completed'],
          ['Driver & Job', 'Who and which job it concerns'],
          ['Penalty if upheld', 'Points added to the driver\'s reliability if you uphold (0 = no penalty, just a record)'],
          ['Driver\'s Response', 'The blue box — the driver\'s explanation. Read this carefully before deciding.'],
        ]} />
      </Section>
      <Section title="Making the decision">
        <p style={s.p}><strong>Uphold</strong> = the report stands. The reliability penalty is applied to the driver and their Profile Score drops. Use when the driver clearly didn't show / cancelled too late without a good reason.</p>
        <p style={s.p}><strong>Dismiss</strong> = the driver is cleared. No penalty. Use when the driver gives a credible explanation (genuine emergency, they actually did show, hirer error). Dismissing also lifts any block stopping the driver re-applying to that job.</p>
        <div style={s.tip}>Dismissing a no-show / "didn't happen" report also counts against the HIRER (their report was wrong). Repeated dismissed reports flag the hirer for review — so dismiss honestly; it's how we keep hirers accountable too.</div>
        <Steps items={[
          'Read the driver\'s response and the dispute type',
          'Decide Uphold or Dismiss',
          'Add a short note explaining why (recommended — it\'s the record)',
          'The driver AND the hirer are notified of the outcome automatically',
        ]} />
        <div style={s.warn}>When it\'s genuinely unclear who is right, do NOT guess — ask your manager before deciding. An unfair penalty drives good drivers off the platform.</div>
      </Section>
      <Section title="Good to know">
        <Table rows={[
          ['Penalties recover', 'The reliability penalty fades automatically over time — it is not permanent.'],
          ['You don\'t email anyone', 'Both parties are notified by the system on resolution.'],
          ['"Penalty 0" disputes', 'Cancelled-with-notice / "didn\'t happen" records carry no penalty — deciding them just sets the record straight.'],
        ]} />
      </Section>
    </>
  ),

  offercodes: (
    <>
      <div style={s.info}>Create and manage promotional / referral codes here. Treat code creation as a manager-approved task.</div>
      <Section title="Creating a code">
        <Steps items={[
          'Confirm the campaign and discount were approved by your manager',
          'Enter the code, discount/benefit, and any expiry or usage limit',
          'Double-check the values before saving — codes may be shared publicly',
          'Click Create / Save',
        ]} />
      </Section>
      <Section title="Managing existing codes">
        <Table rows={[
          ['Active', 'Currently usable by users'],
          ['Disable / Expire', 'Stops further use — reversible where the UI allows'],
          ['Usage count', 'How many times a code has been redeemed'],
        ]} />
        <p style={s.p}>Do not delete or disable a live campaign code without manager approval — users may be mid-signup with it.</p>
      </Section>
    </>
  ),

  jobs: (
    <>
      <div style={s.tip}>Main daily task: check the 🔴 Overdue tab first thing every day.</div>
      <Section title="Tabs">
        <Table rows={[
          ['🔴 Overdue', 'Past end date, not closed — your priority'],
          ['🔵 Assigned', 'Active jobs in progress'],
          ['🟢 Open', 'Accepting applications'],
          ['✅ Completed', 'Finished jobs'],
          ['⚫ Expired/Closed', 'Ended without completion'],
        ]} />
      </Section>
      <Section title="Handling an overdue job">
        <Steps items={[
          'Click the job card to see full details in the right panel',
          'Check: was driver assigned? Is there insurance? Was a nudge sent?',
          'Option A — Mark Complete: job was done, hirer forgot to close it',
          'Option B — Mark Expired: job was never started or something went wrong',
          'If unsure → ask your manager before clicking',
        ]} />
      </Section>
      <Section title="⚠ No Insurance warning">
        <div style={s.warn}>If a job shows "⚠ No Insurance", contact the hirer by email immediately to confirm insurance coverage. Do not ignore this.</div>
      </Section>
    </>
  ),

  users: (
    <>
      <Section title="Finding a user">
        <p style={s.p}>Search by name, email, phone, or company name. Filter by Role (Driver/Hirer) or Status (Active/Suspended/etc.).</p>
        <p style={s.p}>Click any row to open the User Details panel on the right.</p>
      </Section>
      <Section title="Columns">
        <Table rows={[
          ['Email Verified', '✅ = clicked link  ❌ = hasn\'t verified  — Legacy = old account'],
          ['CDL / Business', 'License status (drivers) or verification status (hirers)'],
          ['Account', '✅ Active  ⛔ Suspended  ⏳ Deletion Pending'],
        ]} />
      </Section>
      <Section title="Account actions">
        <Table rows={[
          ['⛔ Suspend', 'Locks user out — reversible — always get manager approval first'],
          ['🔓 Reactivate', 'Restores access, or cancels a deletion request'],
          ['🗑️ Delete', 'PERMANENT — all data gone — must type full name to confirm'],
        ]} />
        <div style={s.warn}>Delete cannot be undone. A PIPEDA confirmation email is sent automatically to the user.</div>
      </Section>
    </>
  ),

  reports: (
    <>
      <div style={s.tip}>Start in 🚨 Pending every day.</div>
      <Section title="Filters">
        <Table rows={[
          ['🚨 Pending', 'New reports — start here'],
          ['🔍 Reviewed', 'Read, awaiting decision'],
          ['✅ Actioned', 'Action was taken'],
        ]} />
      </Section>
      <Section title="Actions">
        <Table rows={[
          ['🔍 Mark Reviewed', 'Read but need more time — no action yet'],
          ['⚠️ Warn User', 'First offence / borderline — sends in-app warning'],
          ['⛔ Suspend User', 'Serious or repeated offence — immediate lockout'],
          ['✖ Dismiss', 'Report is invalid, false, or already resolved'],
        ]} />
      </Section>
      <Section title="Admin Notes">
        <p style={s.p}>Write your notes before taking action (e.g. "User has 2 prior warnings"). Click Save Notes. Notes are internal — the reported user cannot see them.</p>
      </Section>
      <Section title="New report banner">
        <p style={s.p}>If a red banner appears — "X new reports arrived while you were reviewing" — click View → immediately. Do not ignore it.</p>
      </Section>
    </>
  ),

  declarations: (
    <>
      <div style={s.info}>Drivers confirm their CDL is valid every day before working. A decline triggers an automatic suspension and an alert here.</div>
      <Section title="Three tabs">
        <Table rows={[
          ['🚨 Admin Alerts', 'Start here — urgent items needing your review'],
          ['⛔ Declines', 'Every time a driver has declined their declaration'],
          ['📋 Full Log', 'Read-only audit trail — investigate specific drivers'],
        ]} />
      </Section>
      <Section title="Handling an alert">
        <Steps items={[
          'Read the alert — check the driver\'s account status',
          'If driver should be reinstated (manager confirmed): click 🔓 Reactivate — this also resolves the alert',
          'If no account action needed: click ✓ Resolve',
          'Only reactivate after manager approval',
        ]} />
      </Section>
      <Section title="Export Court-Ready Log">
        <Steps items={[
          'Go to Full Declaration Log tab',
          'Find the driver and click their shortened UID to copy it',
          'Paste UID into the orange export panel',
          'Confirm the driver name appears in green',
          'Click 📄 Export & Print → use Cmd+P / Ctrl+P to save as PDF',
        ]} />
        <p style={s.p}>Use only when legal or insurance documentation is requested.</p>
      </Section>
    </>
  ),

  insurance: (
    <>
      <div style={s.info}>This screen is mostly read-only. You act only when a missing confirmation alert appears.</div>
      <Section title="What you see">
        <p style={s.p}>Records grouped by hirer and job. For each driver: confirmation date, shift start, re-hire status, and acknowledgement checkmarks.</p>
        <p style={s.p}>All checkmarks should be ✓ green. If any are ✗ red, note it and tell your manager.</p>
      </Section>
      <Section title="Missing Confirmation alerts (red banner)">
        <div style={s.warn}>A driver started a job without the hirer confirming insurance. This is a compliance issue — do not ignore it.</div>
        <Steps items={[
          'Read who the hirer and driver are',
          'Contact the hirer by email to submit their insurance confirmation',
          'Click Dismiss on the alert once resolved',
          'Click Dismiss all if all alerts are resolved',
        ]} />
      </Section>
      <Section title="Filters">
        <p style={s.p}>Filter by hirer name, driver name, or date range. Click Clear to reset.</p>
      </Section>
    </>
  ),

  notifications: (
    <>
      <div style={s.warn}>Always get manager approval before sending — notifications go to potentially thousands of users and cannot be recalled.</div>
      <Section title="How to send">
        <Steps items={[
          'Choose audience: All users, Drivers only, or Hirers only',
          'Click a template (recommended) or write your own title + message',
          'Re-read the title and message — confirm the right audience is selected',
          'Click Send Notification',
        ]} />
      </Section>
      <Section title="Templates available">
        <Table rows={[
          ['⚠️ License Expiring Soon', 'Drivers whose CDL is close to expiry'],
          ['✅ License Verified', 'Confirmation of CDL approval'],
          ['❌ License Rejected', 'Notification of rejection'],
          ['🚛 New Jobs Available', 'Alert drivers to new postings'],
          ['⛔ Account Suspended', 'Suspension notification'],
          ['🎉 Welcome to Crewboard', 'New user welcome'],
        ]} />
      </Section>
      <Section title="Notification History">
        <p style={s.p}>Check the right panel before sending to avoid duplicate notifications. You can see sender, time, and audience for all past notifications.</p>
      </Section>
    </>
  ),

  admins: (
    <>
      <div style={s.info}>This screen is visible to superadmin accounts only.</div>
      <Section title="Creating a new admin">
        <Steps items={[
          'Fill in: Email, Password (temporary), Full Name, Role',
          'Role = "admin" for regular; "superadmin" only on manager instruction',
          'Click Create Admin',
          'Share the credentials securely — NOT by email',
        ]} />
      </Section>
      <Section title="Deleting an admin">
        <Steps items={[
          'Find the admin in the table',
          'Click Delete next to their name',
          'Confirm the deletion',
        ]} />
        <p style={s.p}>Only delete when someone leaves the team or access needs to be removed. You cannot delete your own account.</p>
      </Section>
    </>
  ),

  subscriptions: (
    <>
      <div style={s.info}>Review and manage user subscription records on this screen.</div>
      <Section title="General guidance">
        <p style={s.p}>Use this screen to verify subscription status for hirers or drivers if they report access issues. Do not modify subscription data without manager approval.</p>
      </Section>
    </>
  ),

  privacy: (
    <>
      <div style={s.info}>PIPEDA compliance screen — handle with care. All deletions are irreversible.</div>
      <Section title="General guidance">
        <p style={s.p}>Use this screen to process user data deletion requests under PIPEDA. Only action requests that have been reviewed and approved by your manager.</p>
        <p style={s.p}>A confirmation email is sent automatically to the user when their data is deleted.</p>
      </Section>
    </>
  ),
};

const PAGE_LABELS = {
  dashboard:     'Dashboard',
  drivers:       'Driver Verification',
  hirers:        'Hirer Verification',
  jobs:          'Job Management',
  users:         'User Management',
  reports:       'Reports Queue',
  disputes:      'Disputes',
  declarations:  'Declarations & Alerts',
  insurance:     'Insurance Confirmations',
  notifications: 'Notifications',
  admins:        'Admin Management',
  subscriptions: 'Subscriptions',
  offercodes:    'Offer Codes',
  privacy:       'Privacy & PIPEDA',
};

const GLOBAL_RULES = (
  <Section title="Rules that apply everywhere">
    <Table rows={[
      ['When in doubt', 'Do nothing — ask your manager first'],
      ['Red buttons', 'Re-read the confirmation popup before clicking'],
      ['Emails', 'System sends them automatically — do NOT send manually'],
      ['Delete', 'Permanent and irreversible on every screen'],
      ['Suspend', 'Always reversible — click Reactivate to undo'],
      ['Notifications', 'Cannot be recalled — get approval first'],
      ['Your login', 'Never share your password with anyone'],
    ]} />
    <div style={s.warn}>If something looks broken: stop clicking, take a screenshot, and tell your manager.</div>
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--surface2)', fontSize: 11, color: 'var(--text-muted)' }}>
      Support: support@crewboard.ca &nbsp;|&nbsp; Legal: legal@crewboard.ca
    </div>
  </Section>
);

export default function JobAidDrawer({ open, onClose, pageId }) {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const content = CONTENT[pageId];
  const label   = PAGE_LABELS[pageId] || 'Job Aid';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 900,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 420,
        background: 'var(--bg)',
        borderLeft: '1px solid var(--surface2)',
        zIndex: 901,
        display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: open ? '-8px 0 32px rgba(0,0,0,0.3)' : 'none',
      }}>

        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--surface2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
              Job Aid
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{label}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, width: 32, height: 32,
              color: 'var(--text-secondary)', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 32px' }}>
          {content || (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No job aid available for this screen yet.</p>
          )}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--surface2)' }}>
            {GLOBAL_RULES}
          </div>
        </div>
      </div>
    </>
  );
}